import LockIcon from '../../assets/icons/fbs/LockIcon.svg';
import ClockIcon from '../../assets/icons/fbs/ClockIcon.svg';
import ClosestIcon from '../../assets/icons/fbs/ClosestIcon.svg';
import LocationIcon1 from '../../assets/icons/fbs/LocationIcon1.svg';
import StarIcon from '../../assets/icons/fbs/StarIcon.svg';
import { useEffect, useState } from 'react';
import { ScanCenter } from '../../api/scans';
import InfoIcon from '../../assets/icons/info.svg';

interface ScanCentreCardProps {
    clinic: ScanCenter;
    isClosest?: boolean;
    isFavourite?: boolean;
    isActive?: boolean;
    onCardClick?: () => void;
    handleClinicDetailsClick?: () => void;
    // Used when restoring Step 1 (back navigation / refresh) so the selected date stays selected.
    initialSelectedDate?: string;
    searchPostcode?: string;
    handleContinueToPersonalDetailsClick?: (
        selectedDate: string,
        siteId: number,
        siteName: string,
        postcode: string,
        centerAddress: string
    ) => void;
}

function getOrdinalSuffix(n: number) {
    const mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 13) return 'th';
    switch (n % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
}

function formatDropdownDateLabel(d: Date) {
    // Target format: "Mon, 4th May"
    const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'short' }).format(d);
    const month = new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(d);
    const day = d.getDate();
    return `${weekday}, ${day}${getOrdinalSuffix(day)} ${month}`;
}

function parseYyyyMmDdToLocalDate(value: string) {
    // Avoid timezone shifts from `new Date("YYYY-MM-DD")` by constructing local date.
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!m) return null;
    const yyyy = Number(m[1]);
    const mm = Number(m[2]);
    const dd = Number(m[3]);
    const d = new Date(yyyy, mm - 1, dd);
    return isNaN(d.getTime()) ? null : d;
}

function generateDateOptions(earliestDate: string | null): { value: string; label: string }[] {
    // NOTE: this helper only generates the "normal date" options based on earliest availability.
    const options: { value: string; label: string }[] = [];
    const start = earliestDate ? new Date(earliestDate) : new Date();

    // If earliestDate is invalid, fall back to next 5 days from today.
    if (isNaN(start.getTime())) {
        const today = new Date();
        // Use the same window as the valid path (30 days, skipping Sundays).
        for (let i = 0; i < 30; i++) {
            const d = new Date(today);
            d.setDate(today.getDate() + i);
            if (d.getDay() === 0) continue; // skip Sunday
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            options.push({ value: `${yyyy}-${mm}-${dd}`, label: formatDropdownDateLabel(d) });
        }
        return options;
    }

    // Use earliestDate + next 30 days, skipping Sundays.
    for (let i = 0; i < 30; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        if (d.getDay() === 0) continue; // skip Sunday

        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        options.push({ value: `${yyyy}-${mm}-${dd}`, label: formatDropdownDateLabel(d) });
    }
    return options;
}

function renderStars(rating: number) {
    const count = Math.round(rating);
    return Array.from({ length: count }, (_, i) => (
        <img key={i} src={StarIcon} alt="Star" />
    ));
}

const ScanCentreCard = ({
    clinic,
    isClosest,
    isFavourite,
    isActive = false,
    onCardClick,
    handleClinicDetailsClick,
    initialSelectedDate,
    searchPostcode = '',
    handleContinueToPersonalDetailsClick,
}: ScanCentreCardProps) => {
    const [selectedDate, setSelectedDate] = useState<string>('');

    // Restore the previously selected date for the active clinic card.
    // Avoid overwriting when user is actively selecting a new date.
    // (This card is usually remounted during Step changes, so this keeps the UX stable.)
    useEffect(() => {
        if (initialSelectedDate && initialSelectedDate !== selectedDate) {
            setSelectedDate(initialSelectedDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSelectedDate]);

    const handleContinue = () => {
        if (handleContinueToPersonalDetailsClick && selectedDate) {
            const siteName = (clinic as any).full_body_name || clinic.name;
            const addressLine =
                clinic.address_without_name?.trim() ||
                [clinic.street, clinic.postcode].filter(Boolean).join(', ');
            const postcode = (clinic.postcode || searchPostcode || '').trim();
            handleContinueToPersonalDetailsClick(selectedDate, clinic.id, siteName, postcode, addressLine);
        }
    };

    const rating = clinic.avg_rating ? parseFloat(clinic.avg_rating) : null;
    const normalDateOptions = generateDateOptions(clinic.earliest_available_date);

    const dateOptions = normalDateOptions;
    const distanceText = clinic.distance != null
        ? `${clinic.distance % 1 === 0 ? clinic.distance : clinic.distance.toFixed(1)} miles away`
        : '';

    return (
        <div className='scan-centre-card-wrapper' >
            <div onClick={onCardClick} className={`scan-centre-card 
        ${isFavourite ? 'favourite-card' : ''}
        ${isClosest ? 'closest-card' : ''}
        `} >
            <div className='scan-centre-card-header'  >
                <div>
                <div>
                <h3>
                {isClosest && (
                    <><img src={ClosestIcon} alt="Closest" />Closest</>
                )}
                &nbsp;
                </h3>
                </div>
                {/* {isFavourite && (
                    <h3><img src={FavouriteIcon} alt="Favourite" />Favourite</h3>
                )} */}
                </div>
                {rating != null && !isNaN(rating) && (
                    <div className='card-rating' >
                    {rating.toFixed(1)}
                    <div className='star-icon-wrapper' >
                        {renderStars(rating)}
                    </div>
                    </div>
                )}
            </div>
            <div className='scan-centre-card-content' >
                <h3 className='card-content-title' >{(clinic as any).full_body_name || clinic.name} <button onClick={handleClinicDetailsClick} className='card-content-title-button' ><img width={18} height={18} alt='View clinic details' src={InfoIcon} /></button></h3>
                {isActive && clinic.latitude && clinic.longitude && (
                    <div className='map-wrapper' >
                        <iframe
                            title={`Map - ${(clinic as any).full_body_name || clinic.name}`}
                            width="100%"
                            height="100%"
                            style={{ border: 0, borderRadius: '8px' }}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${clinic.latitude},${clinic.longitude}&zoom=15`}
                        />
                    </div>
                )}
                <div className='card-content-items-wrapper' >
                    <div className='card-content-item' >
                        <img src={LocationIcon1} alt="Location" />
                        <div>
                            {distanceText}
                            <div className='card-address' >{clinic.address_without_name || `${clinic.street}, ${clinic.postcode}`}</div>
                        </div>
                    </div>
                    {/* Speak to us (hidden for now - per Figma update) */}
                </div>

                {isActive &&<div className='scan-centre-card-date-select-wrapper' >
                    <select
                        className='scan-centre-card-select'
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                    >
                        <option value="">Request date</option>
                        {dateOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    {selectedDate && (
                        <div className="scan-centre-card-date-requested">
                            <>Date requested: {(() => {
                                const d = parseYyyyMmDdToLocalDate(selectedDate);
                                return d ? formatDropdownDateLabel(d) : selectedDate;
                            })()}</>
                        </div>
                    )}
                    {selectedDate && <button className='scan-centre-card-select-button' onClick={handleContinue} >Continue to Personal Details</button>}
                </div>}

                {selectedDate && isActive && (
                    <div className="card-footer-wrapper" >
                        <div className="card-footer-wrapper-item" >
                            <img src={LockIcon} alt="Icon 1" />
                            <div>Secure booking </div>
                        </div>
                        <div className="card-footer-wrapper-item" >
                            <img src={ClockIcon} alt="Icon 1" />
                            <div>Free cancellation up to 48 hours</div>
                        </div>
                    </div>
                )}
            </div>
        </div>
        </div>
    )
}

export default ScanCentreCard;
